/**
 * 读取图片的像素尺寸与 DPI（分辨率）。
 *
 * 只解析文件头，不做整图解码：
 *  - PNG：签名 + IHDR（宽高）+ pHYs（每米的像素数）
 *  - JPEG：SOI + APP0(JFIF) / APP1(EXIF)（DPI）+ SOFn（宽高）
 *
 * 读取不到 DPI 时回退到 DEFAULT_PRINT_DPI。
 */

/** 未包含分辨率信息时的默认打印 DPI（与编辑器推荐的 300 DPI 保持一致） */
export const DEFAULT_PRINT_DPI = 300;

/** 头部解析的最大字节数，避免大文件被整体读入内存 */
const HEADER_READ_BYTES = 262144;

const CM_PER_INCH = 2.54;
/** PNG pHYs 单位为「每米像素数」，换算成每英寸需 ×0.0254 */
const PIXELS_PER_METER_TO_DPI = 0.0254;

const PNG_SIGNATURE = 0x89504e47;
const PNG_CHUNK_PHYS = 0x70485973; // 'pHYs'
const PNG_CHUNK_IEND = 0x49454e44; // 'IEND'

const JPEG_SOI = 0xffd8; // 用于 getUint16 判断文件头
const JPEG_SOS = 0xda; // 以下均为标记字节（不含 0xFF 前缀）
const JPEG_EOI = 0xd9;
const JPEG_APP0 = 0xe0;
const JPEG_APP1 = 0xe1;
const JFIF_IDENTIFIER = 0x4a464946; // 'JFIF'
const EXIF_IDENTIFIER = 0x45786966; // 'Exif'

/**
 * 解析 PNG 的宽高与 DPI
 * @param {DataView} view
 * @returns {{width:number, height:number, dpiX:number, dpiY:number}|null}
 */
function readPngMeta(view) {
  if (view.byteLength < 24 || view.getUint32(0) !== PNG_SIGNATURE) return null;

  // 第一个 chunk 必为 IHDR，宽高分别位于偏移 16、20
  const width = view.getUint32(16);
  const height = view.getUint32(20);
  let dpiX = 0;
  let dpiY = 0;

  let offset = 8;
  while (offset + 8 <= view.byteLength) {
    const length = view.getUint32(offset);
    const type = view.getUint32(offset + 4);

    if (type === PNG_CHUNK_PHYS) {
      if (offset + 17 > view.byteLength) break;
      const pixelsPerUnitX = view.getUint32(offset + 8);
      const pixelsPerUnitY = view.getUint32(offset + 12);
      const unit = view.getUint8(offset + 16); // 1 = 米
      if (unit === 1) {
        dpiX = pixelsPerUnitX * PIXELS_PER_METER_TO_DPI;
        dpiY = pixelsPerUnitY * PIXELS_PER_METER_TO_DPI;
      }
      break;
    }
    if (type === PNG_CHUNK_IEND) break;

    // chunk = 4(length) + 4(type) + length + 4(crc)
    offset += 12 + length;
  }

  return { width, height, dpiX, dpiY };
}

/**
 * 解析 EXIF 中的分辨率（IFD0 的 XResolution / YResolution / ResolutionUnit）
 * @param {DataView} view
 * @param {number} start 指向 APP1 数据段的 "Exif\0\0"
 * @returns {{dpiX:number, dpiY:number}|null}
 */
function readExifDpi(view, start) {
  if (start + 6 > view.byteLength) return null;
  if (view.getUint32(start) !== EXIF_IDENTIFIER) return null;
  if (view.getUint16(start + 4) !== 0x0000) return null;

  const tiff = start + 6;
  if (tiff + 8 > view.byteLength) return null;

  const endian = view.getUint16(tiff);
  const little = endian === 0x4949; // 'II'
  if (!little && endian !== 0x4d4d) return null; // 'MM'

  const get16 = (o) => view.getUint16(o, little);
  const get32 = (o) => view.getUint32(o, little);

  if (get16(tiff + 2) !== 0x002a) return null;
  const ifd0 = tiff + get32(tiff + 4);
  if (ifd0 + 2 > view.byteLength) return null;

  const count = get16(ifd0);
  let xRes = 0;
  let yRes = 0;
  let unit = 2; // 默认英寸

  for (let i = 0; i < count; i++) {
    const entry = ifd0 + 2 + i * 12;
    if (entry + 12 > view.byteLength) break;
    const tag = get16(entry);
    if (tag !== 0x011a && tag !== 0x011b && tag !== 0x0128) continue;

    const type = get16(entry + 2);
    const num = get32(entry + 4);
    let value = 0;
    if (type === 5 && num === 1) {
      // RATIONAL：值为指向 2 个 uint32 的偏移
      const valueOffset = tiff + get32(entry + 8);
      if (valueOffset + 8 > view.byteLength) continue;
      const denominator = get32(valueOffset + 4);
      value = denominator ? get32(valueOffset) / denominator : 0;
    } else if (type === 3 && num === 1) {
      value = get16(entry + 8);
    } else {
      value = get32(entry + 8);
    }

    if (tag === 0x011a) xRes = value;
    else if (tag === 0x011b) yRes = value;
    else unit = value;
  }

  if (!xRes || !yRes) return null;
  // ResolutionUnit：2=英寸，3=厘米
  const factor = unit === 3 ? CM_PER_INCH : 1;
  return { dpiX: xRes * factor, dpiY: yRes * factor };
}

/**
 * 解析 JPEG 的宽高与 DPI
 * @param {DataView} view
 * @returns {{width:number, height:number, dpiX:number, dpiY:number}|null}
 */
function readJpegMeta(view) {
  if (view.byteLength < 4 || view.getUint16(0) !== JPEG_SOI) return null;

  let width = 0;
  let height = 0;
  let dpiX = 0;
  let dpiY = 0;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) {
      offset++;
      continue;
    }
    const marker = view.getUint8(offset + 1);

    // 0xFF 填充
    if (marker === 0xff) {
      offset++;
      continue;
    }
    // 无长度字段的标记
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      offset += 2;
      continue;
    }
    if (marker === JPEG_SOS || marker === JPEG_EOI) break;

    const segmentLength = view.getUint16(offset + 2);
    if (segmentLength < 2) break;
    const dataOffset = offset + 4;

    const isSof =
      marker >= 0xc0 &&
      marker <= 0xcf &&
      marker !== 0xc4 &&
      marker !== 0xc8 &&
      marker !== 0xcc;
    if (isSof && dataOffset + 5 <= view.byteLength) {
      height = view.getUint16(dataOffset + 1);
      width = view.getUint16(dataOffset + 3);
    } else if (marker === JPEG_APP0 && !dpiX && dataOffset + 12 <= view.byteLength) {
      // JFIF：单位(1=DPI, 2=DPCM) + X/Y 密度
      if (view.getUint32(dataOffset) === JFIF_IDENTIFIER) {
        const units = view.getUint8(dataOffset + 7);
        const xDensity = view.getUint16(dataOffset + 8);
        const yDensity = view.getUint16(dataOffset + 10);
        if (units === 1) {
          dpiX = xDensity;
          dpiY = yDensity;
        } else if (units === 2) {
          dpiX = xDensity * CM_PER_INCH;
          dpiY = yDensity * CM_PER_INCH;
        }
      }
    } else if (marker === JPEG_APP1 && !dpiX) {
      const exif = readExifDpi(view, dataOffset);
      if (exif) {
        dpiX = exif.dpiX;
        dpiY = exif.dpiY;
      }
    }

    offset += 2 + segmentLength;
    if (width && height && dpiX) break;
  }

  return { width, height, dpiX, dpiY };
}

/**
 * 兜底：通过解码获取像素尺寸（无法从头部解析出宽高时使用）
 * @param {Blob} blob
 * @returns {Promise<{width:number, height:number}>}
 */
async function decodeDimensions(blob) {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close?.();
    return size;
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('无法解码图片尺寸'));
    };
    img.src = url;
  });
}

/**
 * 读取图片文件的像素尺寸与 DPI
 * @param {Blob|File} file
 * @returns {Promise<{width:number, height:number, dpiX:number, dpiY:number}>}
 */
export async function readImageMeta(file) {
  let meta = null;
  try {
    const buffer = await file.slice(0, HEADER_READ_BYTES).arrayBuffer();
    const view = new DataView(buffer);
    if (view.byteLength >= 8 && view.getUint32(0) === PNG_SIGNATURE) {
      meta = readPngMeta(view);
    } else if (view.byteLength >= 2 && view.getUint16(0) === JPEG_SOI) {
      meta = readJpegMeta(view);
    }
  } catch (e) {
    meta = null;
  }

  let width = meta?.width || 0;
  let height = meta?.height || 0;
  if (!width || !height) {
    const size = await decodeDimensions(file);
    width = size.width;
    height = size.height;
  }

  const dpiX = meta?.dpiX > 0 ? meta.dpiX : DEFAULT_PRINT_DPI;
  const dpiY = meta?.dpiY > 0 ? meta.dpiY : DEFAULT_PRINT_DPI;

  return { width, height, dpiX, dpiY };
}
